import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Star, Eye, EyeOff } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  fetchAdminVideos,
  createVideo,
  updateVideo,
  deleteVideo,
  getSubmissions,
  getSubscribers,
} from "../../api";
import UploadButton from "./UploadButton";

const inputCls =
  "bg-[#0d0d0d] border-white/25 text-white font-comfortaa text-xs placeholder:text-white/35";

function VideoRow({ video, onSaved, onDeleted }) {
  const [v, setV] = useState({ ...video });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await updateVideo(v.id, {
        title: v.title,
        thumbnail: v.thumbnail,
        videoUrl: v.videoUrl,
        section: v.section,
        featured: v.featured,
        hidden: !!v.hidden,
        order: Number(v.order) || 0,
      });
      toast.success("Video updated");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${v.title}"?`)) return;
    try {
      await deleteVideo(v.id);
      toast.success("Video deleted");
      onDeleted();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className={`bg-[#1c1c1c] border border-white/10 rounded-lg p-4 mb-4 ${v.hidden ? "opacity-60" : ""}`} data-testid={`admin-video-row-${v.id}`} data-hidden={v.hidden ? "true" : "false"}>
      <div className="flex gap-4">
        <img src={v.thumbnail} alt="" className="w-24 h-14 object-cover rounded-md shrink-0 hidden sm:block" />
        <div className="flex-1 grid gap-2">
          <div className="grid md:grid-cols-2 gap-2">
            <Input value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} placeholder="Title" className={inputCls} data-testid={`video-title-${v.id}`} />
            <div className="flex gap-2 items-center">
              <Select value={v.section} onValueChange={(val) => setV({ ...v, section: val })}>
                <SelectTrigger className={`${inputCls} w-[130px]`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1c1c1c] border-white/20 text-white font-comfortaa">
                  <SelectItem value="portfolio">Portfolio</SelectItem>
                  <SelectItem value="verticals">Verticals</SelectItem>
                  <SelectItem value="fun">Just For Fun</SelectItem>
                </SelectContent>
              </Select>
              <Input type="number" value={v.order} onChange={(e) => setV({ ...v, order: e.target.value })} className={`${inputCls} w-[70px]`} title="Order" />
              <div className="flex items-center gap-1.5" title="Show on homepage">
                <Star size={13} className={v.featured ? "text-yellow-400" : "text-white/30"} fill={v.featured ? "currentColor" : "none"} />
                <Switch checked={v.featured} onCheckedChange={(c) => setV({ ...v, featured: c })} data-testid={`video-featured-${v.id}`} />
              </div>
              <div className="flex items-center gap-1.5" title={v.hidden ? "Hidden from the site" : "Visible on the site"}>
                {v.hidden ? <EyeOff size={13} className="text-white/30" /> : <Eye size={13} className="text-[#8fae4f]" />}
                <Switch checked={!v.hidden} onCheckedChange={(c) => setV({ ...v, hidden: !c })} data-testid={`video-visible-${v.id}`} />
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Input value={v.thumbnail} onChange={(e) => setV({ ...v, thumbnail: e.target.value })} placeholder="Thumbnail image URL" className={inputCls} data-testid={`video-thumbnail-${v.id}`} />
            <UploadButton
              kind="image"
              label="Upload image"
              testId={`thumbnail-upload-${v.id}`}
              onUploaded={(url) => setV((prev) => ({ ...prev, thumbnail: url }))}
            />
          </div>
          <div className="flex gap-2">
            <Input value={v.videoUrl} onChange={(e) => setV({ ...v, videoUrl: e.target.value })} placeholder="Video URL (mp4 or YouTube)" className={inputCls} />
            <UploadButton
              label="Upload"
              testId={`video-upload-${v.id}`}
              onUploaded={(url) => setV((prev) => ({ ...prev, videoUrl: url }))}
            />
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <Button size="sm" onClick={save} disabled={busy} className="bg-white text-black hover:bg-white/85 h-8 px-3" data-testid={`video-save-${v.id}`}>
            <Save size={13} />
          </Button>
          <Button size="sm" onClick={remove} variant="outline" className="border-red-400/50 text-red-400 hover:bg-red-400/10 hover:text-red-300 h-8 px-3 bg-transparent" data-testid={`video-delete-${v.id}`}>
            <Trash2 size={13} />
          </Button>
        </div>
      </div>
    </div>
  );
}

export function VideosEditor({ onSaved }) {
  const [videos, setVideos] = useState([]);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ title: "", thumbnail: "", videoUrl: "", section: "portfolio", featured: false, order: 99 });

  const load = useCallback(async () => {
    try {
      const v = await fetchAdminVideos();
      setVideos(v);
    } catch {
      toast.error("Could not load videos");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const refreshAll = () => {
    load();
    onSaved && onSaved();
  };

  const addVideo = async () => {
    if (!draft.title.trim() || !draft.videoUrl.trim()) {
      toast.error("Title and video URL are required");
      return;
    }
    const sectionOrders = videos.filter((x) => x.section === draft.section).map((x) => Number(x.order) || 0);
    const order = sectionOrders.length ? Math.min(...sectionOrders) - 1 : 0;
    try {
      await createVideo({ ...draft, order });
      toast.success("Video added");
      setDraft({ title: "", thumbnail: "", videoUrl: "", section: "portfolio", featured: false, order: 99 });
      setAdding(false);
      refreshAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Add failed");
    }
  };

  return (
    <div data-testid="editor-videos">
      <div className="flex items-center justify-between mb-5">
        <p className="font-comfortaa text-white/60 text-xs" data-testid="videos-summary">
          {videos.length} videos · {videos.filter((x) => x.hidden).length} hidden · Star = homepage Sample Work · Eye = visible on site
        </p>
        <Button onClick={() => setAdding(!adding)} className="bg-white text-black hover:bg-white/85 font-comfortaa text-xs" data-testid="add-video-button">
          <Plus size={14} className="mr-1" /> Add video
        </Button>
      </div>

      {adding && (
        <div className="bg-[#232323] border border-white/20 rounded-lg p-4 mb-6" data-testid="add-video-form">
          <p className="font-comfortaa text-white text-xs mb-3">New video</p>
          <div className="grid gap-2">
            <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="Title" className={inputCls} data-testid="new-video-title" />
            <div className="flex gap-2 items-center">
              {draft.thumbnail && (
                <img src={draft.thumbnail} alt="" className="w-16 h-9 object-cover rounded shrink-0" data-testid="new-video-thumbnail-preview" />
              )}
              <Input value={draft.thumbnail} onChange={(e) => setDraft({ ...draft, thumbnail: e.target.value })} placeholder="Thumbnail image URL" className={inputCls} data-testid="new-video-thumbnail" />
              <UploadButton
                kind="image"
                label="Upload image"
                testId="new-thumbnail-upload"
                onUploaded={(url) => setDraft((prev) => ({ ...prev, thumbnail: url }))}
              />
            </div>
            <div className="flex gap-2">
              <Input value={draft.videoUrl} onChange={(e) => setDraft({ ...draft, videoUrl: e.target.value })} placeholder="Video URL (mp4 or YouTube)" className={inputCls} data-testid="new-video-url" />
              <UploadButton
                label="Upload"
                testId="new-video-upload"
                onUploaded={(url) => setDraft((prev) => ({ ...prev, videoUrl: url }))}
              />
            </div>
            <div className="flex gap-3 items-center">
              <Select value={draft.section} onValueChange={(val) => setDraft({ ...draft, section: val })}>
                <SelectTrigger className={`${inputCls} w-[140px]`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1c1c1c] border-white/20 text-white font-comfortaa">
                  <SelectItem value="portfolio">Portfolio</SelectItem>
                  <SelectItem value="verticals">Verticals</SelectItem>
                  <SelectItem value="fun">Just For Fun</SelectItem>
                </SelectContent>
              </Select>
              <span className="font-comfortaa text-white/50 text-xs">Featured</span>
              <Switch checked={draft.featured} onCheckedChange={(c) => setDraft({ ...draft, featured: c })} />
              <Button onClick={addVideo} className="bg-white text-black hover:bg-white/85 font-comfortaa text-xs ml-auto" data-testid="new-video-submit">
                Add
              </Button>
            </div>
          </div>
        </div>
      )}

      {videos.map((v) => (
        <VideoRow key={v.id} video={v} onSaved={refreshAll} onDeleted={refreshAll} />
      ))}
    </div>
  );
}

/* ---------- Submissions ---------- */
export function SubmissionsPanel() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    getSubmissions().then(setRows).catch(() => toast.error("Could not load submissions"));
  }, []);

  if (rows === null) return <p className="font-comfortaa text-white/50 text-sm">Loading…</p>;
  if (rows.length === 0)
    return <p className="font-comfortaa text-white/50 text-sm" data-testid="no-submissions">No contact submissions yet.</p>;

  return (
    <div className="space-y-4" data-testid="submissions-list">
      {rows.map((r) => (
        <div key={r.id} className="bg-[#1c1c1c] border border-white/10 rounded-lg p-5">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
            <p className="font-comfortaa text-white text-sm">
              {r.name} <span className="text-white/50 text-xs ml-2">{r.email}</span>
              {r.company && <span className="text-white/40 text-xs ml-2">· {r.company}</span>}
            </p>
            <p className="font-comfortaa text-white/40 text-[11px]">{new Date(r.created_at).toLocaleString()}</p>
          </div>
          <p className="font-comfortaa text-white/80 text-sm leading-relaxed">{r.message}</p>
          {r.referral && (
            <p className="font-comfortaa text-white/40 text-xs mt-2">Heard about us via: {r.referral}</p>
          )}
        </div>
      ))}
    </div>
  );
}

/* ---------- Subscribers ---------- */
export function SubscribersPanel() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    getSubscribers().then(setRows).catch(() => toast.error("Could not load subscribers"));
  }, []);

  if (rows === null) return <p className="font-comfortaa text-white/50 text-sm">Loading…</p>;
  if (rows.length === 0)
    return <p className="font-comfortaa text-white/50 text-sm" data-testid="no-subscribers">No newsletter subscribers yet.</p>;

  return (
    <div className="bg-[#1c1c1c] border border-white/10 rounded-lg overflow-hidden" data-testid="subscribers-list">
      <table className="w-full">
        <thead>
          <tr className="border-b border-white/10">
            <th className="font-comfortaa text-white/50 text-xs text-left px-5 py-3">Name</th>
            <th className="font-comfortaa text-white/50 text-xs text-left px-5 py-3">Email</th>
            <th className="font-comfortaa text-white/50 text-xs text-left px-5 py-3">Date</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-white/5">
              <td className="font-comfortaa text-white/85 text-xs px-5 py-3">{`${r.firstName} ${r.lastName}`.trim() || "—"}</td>
              <td className="font-comfortaa text-white/85 text-xs px-5 py-3">{r.email}</td>
              <td className="font-comfortaa text-white/45 text-xs px-5 py-3">{new Date(r.created_at).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
